<template>
  <div class="start-flow-page">
    <div class="flow-header-card" v-if="flowInfo">
      <div class="flow-icon">
        <div class="flow-icon-circle">{{ flowInfo.title ? flowInfo.title.substr(0, 1) : '流' }}</div>
      </div>
      <div class="flow-meta">
        <div class="flow-title">{{ flowInfo.title }}</div>
        <div class="flow-code">流程代码：{{ flowInfo.formCode }}</div>
        <div class="flow-desc">{{ flowInfo.description }}</div>
      </div>
    </div>

    <div class="form-card">
      <div class="section-title">流程表单</div>
      <div v-if="loading" class="loading">加载表单中...</div>
      <form v-else @submit.prevent="handleSubmit">
        <div v-for="widget in visibleWidgets" :key="widget.key" class="form-item">
          <label class="field-label">
            {{ widget.options.label }}
            <span v-if="widget.options.required" class="required">*</span>
          </label>

          <!-- 文本输入 -->
          <input
            v-if="widget.type === 'input'"
            :value="getTextInputValue(widget.options.name)"
            :disabled="!!widget.options.disabled"
            :placeholder="widget.options.placeholder"
            :readonly="!!widget.options.readonly"
            class="field-input"
            @input="onTextInput(widget, $event.target.value)"
            type="text"
          />

          <!-- 文本域 -->
          <textarea
            v-else-if="widget.type === 'textarea'"
            :value="getTextInputValue(widget.options.name)"
            :disabled="!!widget.options.disabled"
            :placeholder="widget.options.placeholder"
            :readonly="!!widget.options.readonly"
            class="field-textarea"
            @input="onTextInput(widget, $event.target.value)"
          ></textarea>

          <!-- 数字输入 -->
          <input
            v-else-if="widget.type === 'number'"
            :value="getNumberInputValue(widget.options.name)"
            :disabled="!!widget.options.disabled || !!widget.options.readonly"
            class="field-input"
            @input="onNumberInput(widget, $event.target.value)"
            type="number"
          />

          <!-- 日期 -->
          <input
            v-else-if="widget.type === 'date'"
            :value="getTextInputValue(widget.options.name)"
            :disabled="!!widget.options.disabled || !!widget.options.readonly"
            class="field-input"
            @change="onDateChange(widget, $event.target.value)"
            type="date"
          />

          <!-- 日期范围 -->
          <div v-else-if="widget.type === 'date-range'" class="date-range-picker">
            <input
              :value="getDateRangeDisplay(widget, 'start')"
              :disabled="!!widget.options.disabled || !!widget.options.readonly"
              class="field-input date-range-input"
              @change="onStartDateChange(widget, $event.target.value)"
              type="date"
            />
            <span class="date-separator">-</span>
            <input
              :value="getDateRangeDisplay(widget, 'end')"
              :disabled="!!widget.options.disabled || !!widget.options.readonly"
              class="field-input date-range-input"
              @change="onEndDateChange(widget, $event.target.value)"
              type="date"
            />
          </div>

          <!-- 下拉选择 -->
          <select
            v-else-if="widget.type === 'select'"
            :value="getTextInputValue(widget.options.name)"
            :disabled="!!widget.options.disabled || !!widget.options.readonly"
            class="field-select"
            @change="onSelectChange(widget, $event.target.value)"
          >
            <option v-for="opt in getNormalizedOptions(widget)" :key="getOptValue(opt)" :value="getOptValue(opt)">
              {{ getOptLabel(opt) }}
            </option>
          </select>

          <!-- 其他类型简单回退为文本输入 -->
          <input
            v-else
            :value="getTextInputValue(widget.options.name)"
            :disabled="!!widget.options.disabled"
            class="field-input"
            @input="onTextInput(widget, $event.target.value)"
            type="text"
          />
        </div>

        <div class="form-actions">
          <button type="submit" class="submit-btn" :disabled="submitting || !canSubmit">提交</button>
          <button type="button" class="cancel-btn" @click="goBack">返回</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { fetchFormConfig, startLowcodeFlow } from '../api/oa';

export default {
  name: 'OAStartFlow',
  data() {
    return {
      flowInfo: null,
      loading: false,
      submitting: false,
      lfFormData: null,
      widgets: [],
      formModel: {},
      dateRangeState: {},
      hiddenFieldNames: {},
    };
  },
  computed: {
    visibleWidgets() {
      return (this.widgets || []).filter(widget => !this.isWidgetHiddenByOaRule(widget));
    },
    canSubmit() {
      return this.visibleWidgets.every(w => {
        if (!w.options || !w.options.required) return true;
        const v = this.formModel[w.options.name];
        return !this.isEmptyValue(v);
      });
    }
  },
  created() {
    this.initFromRoute();
  },
  methods: {
    initFromRoute() {
      const paramsStr = this.$route.query.params;
      if (!paramsStr) {
        this.$message.error('缺少流程参数');
        this.goBack();
        return;
      }
      try {
        const obj = JSON.parse(decodeURIComponent(paramsStr));
        this.flowInfo = obj || {};
        if (!this.flowInfo.formCode) {
          this.$message.error('未获取到流程编码');
          return;
        }
        this.loadFormConfig();
      } catch (e) {
        console.error('解析流程参数失败', e);
        this.$message.error('流程参数异常');
      }
    },
    async loadFormConfig() {
      this.loading = true;
      try {
        const res = await fetchFormConfig(this.flowInfo.formCode);
        if (res && res.code === 200 && res.data) {
          this.lfFormData = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
          const widgetList = (this.lfFormData && this.lfFormData.widgetList) || [];
          this.widgets = widgetList;
          this.initFormModel();
        } else {
          this.$message.error(res && res.msg ? res.msg : '获取表单配置失败');
        }
      } catch (e) {
        console.error('获取表单配置失败', e);
        this.$message.error('获取表单配置失败');
      } finally {
        this.loading = false;
      }
    },
    initFormModel() {
      this.formModel = {};
      this.dateRangeState = {};
      this.hiddenFieldNames = {};
      (this.widgets || []).forEach(w => {
        if (!w.options || !w.options.name) return;
        const name = w.options.name;
        if (w.type === 'date-range') {
          const { startDate, endDate } = this.normalizeDateRangeValue(w.options.defaultValue);
          this.setDateRangeValue(w, startDate || w.options.defaultStartDate, endDate || w.options.defaultEndDate);
          return;
        }

        if (w.type === 'number') {
          this.$set(this.formModel, name, this.normalizeNumberValue(w.options.defaultValue, w));
          return;
        }

        const defaultValue = w.options.defaultValue;
        this.$set(this.formModel, name, defaultValue !== undefined && defaultValue !== null ? defaultValue : '');
      });
      this.applyOaSelectVisibilityRules();
    },
    getTextInputValue(fieldName) {
      const value = this.formModel[fieldName];
      return value === undefined || value === null ? '' : value;
    },
    getNumberInputValue(fieldName) {
      const value = this.formModel[fieldName];
      if (value === undefined || value === null || value === '') {
        return '0';
      }
      return String(value);
    },
    normalizeNumberValue(value, widget) {
      if (value === '' || value === null || value === undefined) {
        const defaultValue = widget && widget.options ? widget.options.defaultValue : undefined;
        return typeof defaultValue === 'number' ? defaultValue : 0;
      }

      const parsedValue = Number(value);
      if (Number.isNaN(parsedValue)) {
        const defaultValue = widget && widget.options ? widget.options.defaultValue : undefined;
        return typeof defaultValue === 'number' ? defaultValue : 0;
      }

      return parsedValue;
    },
    normalizeDateRangeValue(value) {
      if (Array.isArray(value)) {
        return {
          startDate: value[0] || '',
          endDate: value[1] || '',
        };
      }

      if (value && typeof value === 'object') {
        return {
          startDate: value.startDate || value.start || '',
          endDate: value.endDate || value.end || '',
        };
      }

      if (typeof value === 'string' && value) {
        try {
          return this.normalizeDateRangeValue(JSON.parse(value));
        } catch (e) {
          const parts = value.split(/\s+-\s+|~|至|,/).map(item => item.trim()).filter(Boolean);
          return {
            startDate: parts[0] || '',
            endDate: parts[1] || '',
          };
        }
      }

      return {
        startDate: '',
        endDate: '',
      };
    },
    setDateRangeValue(widget, startDate, endDate) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      if (!fieldName) return;

      this.$set(this.dateRangeState, fieldName, {
        startDate: startDate || '',
        endDate: endDate || '',
      });
      this.$set(this.formModel, fieldName, startDate && endDate ? [startDate, endDate] : '');
    },
    getDateRangeDisplay(widget, side) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      if (!fieldName) return '';
      const rangeState = this.dateRangeState[fieldName] || {};
      return side === 'start' ? rangeState.startDate || '' : rangeState.endDate || '';
    },
    cloneFieldValue(fieldName) {
      const currentValue = this.formModel[fieldName];
      if (Array.isArray(currentValue)) {
        return currentValue.slice();
      }
      if (currentValue && typeof currentValue === 'object') {
        return { ...currentValue };
      }
      return currentValue;
    },
    isEmptyValue(value) {
      if (Array.isArray(value)) {
        return value.length === 0 || value.every(item => item === undefined || item === null || item === '');
      }
      return value === undefined || value === null || value === '';
    },
    setFieldValue(fieldName, value) {
      const widget = (this.widgets || []).find(item => item && item.options && item.options.name === fieldName);
      if (!widget) return;

      if (widget.type === 'date-range') {
        const { startDate, endDate } = this.normalizeDateRangeValue(value);
        this.setDateRangeValue(widget, startDate, endDate);
        return;
      }

      if (widget.type === 'number') {
        this.$set(this.formModel, fieldName, this.normalizeNumberValue(value, widget));
        return;
      }

      this.$set(this.formModel, fieldName, value);
    },
    getOaSelectVisibilityRules() {
      const rules = this.lfFormData && this.lfFormData.formConfig && Array.isArray(this.lfFormData.formConfig.oaRules)
        ? this.lfFormData.formConfig.oaRules
        : [];
      return rules.filter(rule => (
        rule &&
        rule.type === 'selectVisibility' &&
        rule.sourceField &&
        rule.targetField &&
        rule.sourceField !== rule.targetField &&
        rule.sourceValue !== undefined &&
        rule.sourceValue !== null &&
        rule.sourceValue !== ''
      ));
    },
    isWidgetHiddenByOaRule(widget) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      return !!(fieldName && this.hiddenFieldNames[fieldName]);
    },
    applyOaSelectVisibilityRules(sourceField = '') {
      const nextHiddenFieldNames = {};
      this.getOaSelectVisibilityRules().forEach(rule => {
        const matched = String(this.formModel[rule.sourceField]) === String(rule.sourceValue);
        const shouldHide = rule.action === 'hide' ? matched : !matched;
        if (shouldHide) {
          nextHiddenFieldNames[rule.targetField] = true;
          if (rule.clearWhenHidden !== false) {
            this.setFieldValue(rule.targetField, '');
          }
        } else {
          delete nextHiddenFieldNames[rule.targetField];
        }
      });
      this.hiddenFieldNames = nextHiddenFieldNames;
    },
    normalizeDateTime(value) {
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.getTime();
      }
      if (typeof value === 'number') {
        return Number.isNaN(value) ? null : value;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const normalized = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
          ? `${trimmed}T00:00:00`
          : (trimmed.includes(' ') ? trimmed.replace(' ', 'T') : trimmed);
        const date = new Date(normalized);
        return Number.isNaN(date.getTime()) ? null : date.getTime();
      }
      return null;
    },
    runDateRangeOrderValidation(widget, showMessage) {
      if (!widget || widget.type !== 'date-range' || !widget.options) {
        return true;
      }

      const value = this.formModel[widget.options.name];
      if (!Array.isArray(value)) {
        return true;
      }

      const [startDate, endDate] = value;
      if (!startDate || !endDate) {
        return true;
      }

      const startTime = this.normalizeDateTime(startDate);
      const endTime = this.normalizeDateTime(endDate);
      if (startTime !== null && endTime !== null && endTime < startTime) {
        if (showMessage) {
          const fieldLabel = widget.options.label || widget.options.name || '日期';
          this.$message.warning(`${fieldLabel}：结束日期必须大于等于开始日期`);
        }
        return false;
      }

      return true;
    },
    createWidgetRef(fieldName) {
      const widget = (this.widgets || []).find(item => item && item.options && item.options.name === fieldName);
      if (!widget) {
        return null;
      }

      const vm = this;
      return {
        get fieldModel() {
          return vm.formModel[fieldName];
        },
        set fieldModel(value) {
          vm.setFieldValue(fieldName, value);
        },
        setValue(value) {
          vm.setFieldValue(fieldName, value);
        },
        updateFieldValue(value) {
          vm.setFieldValue(fieldName, value);
        },
        validateField() {
          return vm.runSingleWidgetValidation(widget, false);
        },
      };
    },
    createRuntimeContext() {
      return {
        formModel: this.formModel,
        globalModel: {
          formModel: this.formModel,
        },
        getWidgetRef: fieldName => this.createWidgetRef(fieldName),
        setFieldValue: (fieldName, value) => this.setFieldValue(fieldName, value),
        $refs: {
          vForm: {
            setFieldValue: (fieldName, value) => this.setFieldValue(fieldName, value),
          },
        },
        $nextTick: this.$nextTick.bind(this),
      };
    },
    executeWidgetScript(widget, eventName, payload) {
      const script = widget && widget.options ? widget.options[eventName] : '';
      if (!script) {
        return undefined;
      }

      const context = this.createRuntimeContext();
      if (eventName === 'onValidate') {
        const executor = new Function('value', 'rule', 'callback', 'formData', 'subFormData', 'rowId', script);
        return executor.call(context, payload.value, null, payload.callback, this.formModel, null, null);
      }

      const executor = new Function('value', 'oldValue', 'subFormData', 'rowId', script);
      return executor.call(context, payload.value, payload.oldValue, null, null);
    },
    triggerWidgetChange(widget, oldValue) {
      try {
        this.executeWidgetScript(widget, 'onChange', {
          value: this.cloneFieldValue(widget.options.name),
          oldValue,
        });
      } catch (e) {
        console.error('字段联动执行失败', e);
        this.$message.error('字段联动执行失败');
      }
    },
    runRequiredValidation(widget, showMessage) {
      if (!widget || !widget.options || !widget.options.required) {
        return true;
      }

      if (!this.isEmptyValue(this.formModel[widget.options.name])) {
        return true;
      }

      if (showMessage) {
        this.$message.warning(widget.options.requiredHint || `${widget.options.label || '当前字段'}不能为空`);
      }
      return false;
    },
    runCustomValidation(widget, showMessage) {
      return new Promise((resolve, reject) => {
        const script = widget && widget.options ? widget.options.onValidate : '';
        if (!script) {
          resolve(true);
          return;
        }

        let resolved = false;
        const done = (result) => {
          if (resolved) return;
          resolved = true;

          if (result instanceof Error) {
            if (showMessage) {
              this.$message.warning(result.message || '校验失败');
            }
            reject(result);
            return;
          }

          if (result === false) {
            const error = new Error((widget.options && widget.options.validationHint) || '校验失败');
            if (showMessage) {
              this.$message.warning(error.message);
            }
            reject(error);
            return;
          }

          resolve(true);
        };

        try {
          const returnValue = this.executeWidgetScript(widget, 'onValidate', {
            value: this.cloneFieldValue(widget.options.name),
            callback: done,
          });

          if (returnValue instanceof Promise) {
            returnValue
              .then(result => done(result))
              .catch(error => done(error instanceof Error ? error : new Error((error && error.message) || '校验失败')));
          } else if (returnValue !== undefined) {
            done(returnValue);
          } else {
            setTimeout(() => {
              if (!resolved) {
                resolve(true);
              }
            }, 0);
          }
        } catch (e) {
          if (showMessage) {
            this.$message.warning((e && e.message) || '校验执行失败');
          }
          reject(e);
        }
      });
    },
    async runSingleWidgetValidation(widget, showMessage, includeRequired = true) {
      if (!widget || !widget.options) {
        return true;
      }
      if (this.isWidgetHiddenByOaRule(widget)) {
        return true;
      }

      if (includeRequired && !this.runRequiredValidation(widget, showMessage)) {
        throw new Error((widget.options && widget.options.requiredHint) || '必填项未填写');
      }

      if (!this.runDateRangeOrderValidation(widget, showMessage)) {
        throw new Error('日期范围校验失败');
      }

      await this.runCustomValidation(widget, showMessage);
      return true;
    },
    async validateAllWidgets(showMessage = true) {
      for (const widget of this.visibleWidgets || []) {
        await this.runSingleWidgetValidation(widget, showMessage, true);
      }
      return true;
    },
    onTextInput(widget, value) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      if (!fieldName) return;
      const oldValue = this.cloneFieldValue(fieldName);
      this.$set(this.formModel, fieldName, value);
      this.triggerWidgetChange(widget, oldValue);
    },
    onNumberInput(widget, value) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      if (!fieldName) return;
      const oldValue = this.cloneFieldValue(fieldName);
      this.$set(this.formModel, fieldName, this.normalizeNumberValue(value, widget));
      this.triggerWidgetChange(widget, oldValue);
    },
    onDateChange(widget, value) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      if (!fieldName) return;
      const oldValue = this.cloneFieldValue(fieldName);
      this.$set(this.formModel, fieldName, value || '');
      this.triggerWidgetChange(widget, oldValue);
      this.runSingleWidgetValidation(widget, true, false).catch(() => {});
    },
    onStartDateChange(widget, value) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      if (!fieldName) return;
      const oldValue = this.cloneFieldValue(fieldName);
      const currentRange = this.dateRangeState[fieldName] || {};
      this.setDateRangeValue(widget, value || '', currentRange.endDate || '');
      this.triggerWidgetChange(widget, oldValue);
      this.runSingleWidgetValidation(widget, true, false).catch(() => {});
    },
    onEndDateChange(widget, value) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      if (!fieldName) return;
      const oldValue = this.cloneFieldValue(fieldName);
      const currentRange = this.dateRangeState[fieldName] || {};
      this.setDateRangeValue(widget, currentRange.startDate || '', value || '');
      this.triggerWidgetChange(widget, oldValue);
      this.runSingleWidgetValidation(widget, true, false).catch(() => {});
    },
    onSelectChange(widget, value) {
      const fieldName = widget && widget.options ? widget.options.name : '';
      if (!fieldName) return;
      const oldValue = this.cloneFieldValue(fieldName);
      const options = this.getNormalizedOptions(widget);
      const matched = options.find(opt => String(this.getOptValue(opt)) === String(value));
      this.$set(this.formModel, fieldName, matched ? this.getOptValue(matched) : value);
      this.applyOaSelectVisibilityRules(fieldName);
      this.triggerWidgetChange(widget, oldValue);
    },
    getNormalizedOptions(widget) {
      if (!widget || !widget.options) return [];
      let opts = widget.options.options;
      if (!opts || !opts.length) {
        opts = widget.options.optionItems || [];
      }
      return opts || [];
    },
    getOptValue(opt) {
      return typeof opt === 'object' ? opt.value : opt;
    },
    getOptLabel(opt) {
      return typeof opt === 'object' ? opt.label : opt;
    },
    async handleSubmit() {
      try {
        await this.validateAllWidgets(true);
      } catch (e) {
        return;
      }

      this.submitting = true;
      try {
        const lfFields = { ...this.formModel };
        const submitData = {
          formCode: this.flowInfo.formCode,
          operationType: 1,
          isLowCodeFlow: true,
          lfFields,
          approversList: lfFields.approversList,
          approversValid: lfFields.approversValid
        };
        const res = await startLowcodeFlow(submitData);
        if (res && res.code === 200) {
          this.$message.success('提交成功');
          // 提交成功后回到 OA 工作台首页，触发统计数据和流程列表的重新加载
          this.$router.replace('/home/oa');
        } else {
          this.$message.error(res && res.msg ? res.msg : '提交失败');
        }
      } catch (e) {
        console.error('提交流程失败', e);
        this.$message.error('提交失败');
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      this.$router.back();
    }
  }
};
</script>

<style scoped>
.start-flow-page {
  height: 100%;
  padding: 16px 24px;
  background: #f5f5f7;
  box-sizing: border-box;
}

.flow-header-card {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.flow-icon {
  margin-right: 12px;
}

.flow-icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: #0d7deb;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
}

.flow-meta {
  flex: 1;
}

.flow-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.flow-code {
  font-size: 13px;
  color: #666;
  margin-bottom: 2px;
}

.flow-desc {
  font-size: 13px;
  color: #999;
}

.form-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
}

.loading {
  padding: 20px 0;
  text-align: center;
  color: #999;
}

.form-item {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 13px;
  margin-bottom: 4px;
}

.required {
  color: #f56c6c;
  margin-left: 4px;
}

.field-input,
.field-textarea,
.field-select {
  width: 100%;
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  padding: 6px 8px;
  font-size: 13px;
}

.field-textarea {
  min-height: 80px;
  resize: vertical;
}

.date-range-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-range-input {
  flex: 1;
  min-width: 0;
}

.date-separator {
  color: #606266;
  font-size: 14px;
  font-weight: 600;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  gap: 8px;
}

.submit-btn,
.cancel-btn {
  flex: 1;
  height: 34px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 13px;
}

.submit-btn {
  background: #0d7deb;
  color: #fff;
}

.submit-btn:disabled {
  background: #b3d4fc;
  cursor: not-allowed;
}

.cancel-btn {
  background: #ebeef5;
  color: #606266;
}
</style>
